'use client';

import { Box, Stack } from '@mui/material';
import { EonAccountStatus } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import { redirect, usePathname } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Link } from '@/components/shared/link';
import { UserCircle } from '@/components/users/userBadge';
import { docsBaseUrl } from '@/contexts/environment';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useUser } from '@/contexts/useUser';

import {
  ASSISTANT_HEIGHT,
  ASSISTANT_MARGIN_BOTTOM,
  AssistantMenuItem,
  MenuItem,
  MoreMenuItem,
  NavigationActions,
  type OverflowMenuItem,
} from './components';
import { UserBox } from './userBox';

import { EONLogo } from '../shared/eonLogo';

const assistantKey = 'assistant';
const dashboardKey = 'dashboard';
const inventoryKey = 'inventory';
const searchKey = 'search';
const jobsKey = 'jobs';
const settingsKey = 'settings';
const storageGatewayKey = 'storage-gateway';
const logSpaceKey = 'log-space';
const lakehouseKey = 'lakehouse';

// Last items in this array will be hidden first when overflow occurs
const itemsPriority = [
  assistantKey,
  dashboardKey,
  inventoryKey,
  searchKey,
  jobsKey,
  settingsKey,
  storageGatewayKey,
  logSpaceKey,
  lakehouseKey,
];

const MENU_ITEM_HEIGHT = 60;
const ASSISTANT_ITEM_HEIGHT = ASSISTANT_HEIGHT + ASSISTANT_MARGIN_BOTTOM;
const BOTTOM_SECTION_HEIGHT = 72; // docs link + navigation actions

export const Navigation = () => {
  const dal = useDAL();
  const { data: projects } = dal.projects.useList();
  const { dlsgFrontend, assistantEnabled, LlgFrontend } = useFeatureFlags();
  const { demoSettings } = useEnvironment();
  const pathname = usePathname();
  const { user, isEmployee } = useUser();
  const [forceOpen, setForceOpen] = useState(false);
  const onboardingMode =
    user?.eonAccount.status === EonAccountStatus.EON_ACCOUNT_STATUS_ONBOARDING;
  const registrationMode =
    user?.eonAccount.status ===
    EonAccountStatus.EON_ACCOUNT_STATUS_REGISTRATION;
  const { isAuthorized, rolesMap, isGlobalRole } = useRoles();

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [overflowKeys, setOverflowKeys] = useState<Set<string>>(new Set());

  const hasAccountRoles = Object.keys(user?.roleToProjectsMappings || {}).some(
    (roleId) => {
      const role = rolesMap[roleId];
      return isGlobalRole(role);
    }
  );

  const allMenuItems = useMemo((): OverflowMenuItem[] => {
    const items: OverflowMenuItem[] = [];

    if (demoSettings.ai || assistantEnabled) {
      items.push({
        key: assistantKey,
        href: '/assistant',
        title: 'Search & explore your data',
        iconClass: '',
        isAssistant: true,
      });
    }

    items.push({
      key: dashboardKey,
      href: '/dashboard',
      iconClass: 'material-symbols-team-dashboard-outline',
      title: 'Dashboard',
      disabled: !isAuthorized('read:dashboard'),
    });

    items.push({
      key: inventoryKey,
      href: '/inventory',
      iconClass: 'material-symbols-inventory-2-outline',
      title: 'Inventory',
      disabled: !isAuthorized('read:inventory'),
    });

    if (!(demoSettings.ai || assistantEnabled)) {
      items.push({
        key: searchKey,
        href: '/search',
        iconClass: 'material-symbols-search',
        title: 'Search',
        disabled: !isAuthorized('read:db') && !isAuthorized('read:files'),
      });
    }

    items.push({
      key: jobsKey,
      href: '/jobs',
      iconClass: 'material-symbols-checklist-rounded',
      title: 'Jobs',
      disabled: !isAuthorized('read:jobs'),
    });

    items.push({
      key: settingsKey,
      href: '/settings',
      iconClass: 'material-symbols-settings-outline-rounded',
      title: 'Backup settings',
      disabled: !isAuthorized('read:backup_settings'),
    });

    if (dlsgFrontend) {
      items.push({
        key: storageGatewayKey,
        href: '/storage-gateway',
        iconClass: 'material-symbols-door-open-outline-rounded',
        title: 'Storage gateway',
        disabled: !isAuthorized('read:policies'),
      });
    }

    if (demoSettings.logSpace || LlgFrontend) {
      items.push({
        key: logSpaceKey,
        href: '/log-space',
        iconClass: 'material-symbols-planet-outline',
        title: 'Log space',
      });
    }

    if (demoSettings.lakehouse) {
      items.push({
        key: lakehouseKey,
        href: '/lakehouse',
        iconClass: 'material-symbols-database-outline',
        title: 'Lakehouse',
      });
    }

    return items;
  }, [demoSettings, assistantEnabled, dlsgFrontend, LlgFrontend, isAuthorized]);

  // Find the currently selected item key based on pathname
  const selectedKey = useMemo(() => {
    for (const item of allMenuItems) {
      if (pathname.startsWith(item.href)) {
        return item.key;
      }
    }
    return null;
  }, [allMenuItems, pathname]);

  // Calculate which items should overflow
  const calculateOverflow = useCallback(() => {
    if (!containerRef.current || !headerRef.current) return;

    const containerHeight = containerRef.current.clientHeight;
    const headerHeight = headerRef.current.clientHeight;
    const availableHeight =
      containerHeight - headerHeight - BOTTOM_SECTION_HEIGHT;

    // Calculate total height needed for all items
    let totalNeeded = 0;
    for (const item of allMenuItems) {
      totalNeeded += item.isAssistant
        ? ASSISTANT_ITEM_HEIGHT
        : MENU_ITEM_HEIGHT;
    }

    // If everything fits, no overflow needed
    if (totalNeeded <= availableHeight) {
      setOverflowKeys(new Set());
      return;
    }

    // We need overflow - reserve space for "more" button
    const availableWithMore = availableHeight - MENU_ITEM_HEIGHT;

    // // Sort items by priority (lowest priority first = first to hide)
    // // Items not in priority list are treated as lowest priority
    const sortedByHidePriority = [...allMenuItems].sort((a, b) => {
      const aPriority = itemsPriority.indexOf(a.key);
      const bPriority = itemsPriority.indexOf(b.key);
      const aScore = aPriority === -1 ? Infinity : aPriority;
      const bScore = bPriority === -1 ? Infinity : bPriority;
      // Higher score = lower priority = hide first
      return bScore - aScore;
    });

    // Determine which items to hide
    const keysToHide = new Set<string>();
    let currentHeight = totalNeeded;

    for (const item of sortedByHidePriority) {
      if (currentHeight <= availableWithMore) break;

      // Never hide the selected item
      if (item.key === selectedKey) continue;

      const itemHeight = item.isAssistant
        ? ASSISTANT_ITEM_HEIGHT
        : MENU_ITEM_HEIGHT;
      keysToHide.add(item.key);
      currentHeight -= itemHeight;
    }

    setOverflowKeys(keysToHide);
  }, [allMenuItems, selectedKey]);

  // Setup ResizeObserver
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      calculateOverflow();
    });

    observer.observe(containerRef.current);
    calculateOverflow(); // Initial calculation

    return () => observer.disconnect();
  }, [calculateOverflow]);

  // Recalculate when menu items or selected item changes
  useEffect(() => {
    calculateOverflow();
  }, [calculateOverflow, allMenuItems, selectedKey]);

  const { visibleItems, overflowItems } = useMemo(() => {
    const visible: OverflowMenuItem[] = [];
    const overflow: OverflowMenuItem[] = [];

    for (const item of allMenuItems) {
      if (overflowKeys.has(item.key)) {
        overflow.push(item);
      } else {
        visible.push(item);
      }
    }

    return { visibleItems: visible, overflowItems: overflow };
  }, [allMenuItems, overflowKeys]);

  useEffect(() => {
    if (
      !user ||
      user.givenName?.toLowerCase().includes('eon support') ||
      isEmployee
    )
      return;

    if (onboardingMode && !pathname.includes('/global-management')) {
      redirect('/global-management');
    } else if (registrationMode) {
      redirect('/onboarding/in-progress');
    }
  }, [onboardingMode, pathname, registrationMode, user, isEmployee]);

  return (
    <Box
      data-testid='navigation'
      className={classNames({
        open: forceOpen,
      })}
      sx={{
        minWidth: '60px',
        maxWidth: '60px',
        position: 'sticky',
        height: '100%',
        zIndex: 1000,

        '& .slide-left, .logo-link *, .user-badge, .docs-link': {
          transition: `opacity 500ms ease-in-out, padding-left 300ms ease-in-out, margin-left 300ms ease-in-out, visibility 500ms ease-in-out`,
        },

        '& a': {
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          height: '60px',
          position: 'relative',
          width: '100%',
        },

        '& .user-box, .navigation-actions, a': {
          pointerEvents: 'none',
        },

        '&:hover, &.open': {
          '& .user-badge': {
            opacity: 0,
          },

          '& .docs-link': {
            opacity: 0,
          },

          '& > div': {
            minWidth: '308px',
          },

          '& .slide-left': {
            opacity: '1 !important',
            visibility: 'visible !important',
            paddingLeft: '3px',
            pointerEvents: 'all',

            a: {
              pointerEvents: 'all',
            },
          },

          '& .logo-link img': {
            marginLeft: '19px !important',
          },
        },
      }}
    >
      <Stack
        ref={containerRef}
        justifyContent='space-between'
        sx={{
          overflow: 'hidden',
          backgroundColor: 'var(--mui-palette-custom-BLACK-01)',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          height: '100%',
          minWidth: '60px',
          transition: 'min-width 300ms ease-in-out',
        }}
      >
        <Stack>
          <Stack
            ref={headerRef}
            className={classNames({
              'min-h-[200px]': !hasAccountRoles,
              'min-h-[280px]': hasAccountRoles,
              'min-h-[300px]': (projects?.projects || []).length > 1,
            })}
          >
            <Link href='/' className='slide-left logo-link'>
              <>
                <EONLogo className='ml-[-16px]' />
                <Box className='w-[24px] h-[24px] absolute left-[-4px] bg-[var(--mui-palette-custom-BLACK-01)]' />
              </>
            </Link>
            <Box className='relative'>
              <Stack
                className='w-[60px] h-[60px] user-badge opacity-1'
                alignItems='center'
                justifyContent='center'
              >
                <UserCircle user={user} />
              </Stack>
              <UserBox
                hasAccountRoles={hasAccountRoles}
                projects={projects?.projects}
                setForceOpen={setForceOpen}
              />
            </Box>
          </Stack>
          <Stack>
            {visibleItems.map((item) =>
              item.isAssistant ? (
                <AssistantMenuItem
                  key={item.key}
                  href={item.href}
                  title={item.title}
                  selected={pathname.startsWith(item.href)}
                />
              ) : (
                <MenuItem
                  key={item.key}
                  href={item.href}
                  iconClass={item.iconClass}
                  title={item.title}
                  disabled={item.disabled}
                  selected={!item.disabled && pathname.startsWith(item.href)}
                />
              )
            )}
            {overflowItems.length > 0 && (
              <MoreMenuItem items={overflowItems} setForceOpen={setForceOpen} />
            )}
          </Stack>
        </Stack>
        <Stack className='relative justify-end pb-[12px]'>
          <Box className='docs-link opacity-1'>
            <MenuItem
              key='docs'
              href={docsBaseUrl}
              iconClass='material-symbols-help-outline-rounded'
              title=''
            />
          </Box>
          <NavigationActions setForceOpen={setForceOpen} />
        </Stack>
      </Stack>
    </Box>
  );
};
